"use client";
import { getApiBase, SystemConfig } from "@/configs";

import { LoginForm } from "@/components/login/login-form";
import { TurnstileWidget } from "@/components/login/turnstile-widget";
import { notifySubscriptionChanged, useSubscription } from "@/components/subscription-provider";
import PublisherConfig from "@/configs/publisher.json";
import { useTranslation } from "@/i18n";
import { isPaidAccessActive } from "@/lib/article-access";
import { getAuthEmail, getAuthToken, handleAuthError, hasAuthToken } from "@/lib/auth-cookie";
import { formatUsdFromCents, getPaidSubscriptions, isPaidEnabled, type SubscribePlan } from "@/lib/paid-plans";
import { isPublisherOwnerEmail, OWNER_SUBSCRIBE_ERROR } from "@/lib/publisher-owner";
import axios from "axios";
import { CheckCircle2, Loader2, Mail, X } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

export type SubscribeModalView = "subscribe" | "support" | "signin";

interface SubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: SubscribeModalView;
  onSignInSuccess?: () => void;
}

type SubscribeSuccessKind = "confirm" | "subscribed";

const API_BASE = getApiBase();

async function requestSubscribe(
  email: string,
  options?: { token?: string | null; plan?: "monthly" | "annual"; successUrl?: string; turnstileToken?: string },
) {
  const plan = options?.plan;
  const successUrl = options?.successUrl;
  return axios.post(
    `${API_BASE}/public/publisher/${PublisherConfig.username}/requestSubscribe`,
    {
      email,
      ...(options?.turnstileToken ? { turnstileToken: options.turnstileToken } : {}),
      ...(plan ? { plan, ...(successUrl ? { successUrl } : {}) } : {}),
    },
    {
      headers: options?.token ? { Authorization: options.token } : undefined,
      validateStatus: (status) => (plan ? status === 200 : status === 200 || status === 201),
    },
  );
}

function currentPageSuccessUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const url = new URL(window.location.href);
  url.searchParams.delete("_book_t");
  url.searchParams.delete("checkout");
  url.searchParams.delete("session_id");
  url.searchParams.delete("signin");
  url.searchParams.delete("subscribe");
  return url.toString();
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function SubscribeModal({ isOpen, onClose, initialView = "subscribe", onSignInSuccess }: SubscribeModalProps) {
  const { t } = useTranslation();
  const publicationName = PublisherConfig.profile.name || PublisherConfig.username;
  const paidEnabled = isPaidEnabled();
  const paid = getPaidSubscriptions();

  const [view, setView] = useState<SubscribeModalView>(initialView);
  const [loginResetToken, setLoginResetToken] = useState(0);
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState<SubscribePlan>("free");
  const [serverAlreadySubscribed, setServerAlreadySubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successKind, setSuccessKind] = useState<SubscribeSuccessKind | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [forceManualForm, setForceManualForm] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const sendingRef = useRef(false);
  const [requestUntil, setRequestUntil] = useState(0);
  const [requestSeconds, setRequestSeconds] = useState(0);
  const { isSiteOwner, isLoading: isOwnerCheckLoading, currentPayment } = useSubscription();

  const startCooldown = (seconds = 60) => {
    setRequestUntil(Date.now() + seconds * 1000);
    setRequestSeconds(seconds);
  };

  useEffect(() => {
    if (!requestUntil) return;
    const tick = () => setRequestSeconds(Math.max(0, Math.ceil((requestUntil - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [requestUntil]);

  useEffect(() => {
    setTurnstileToken("");
    setTurnstileResetKey((value) => value + 1);
  }, [isOpen, view]);

  useEffect(() => {
    if (!isOpen) {
      setForceManualForm(false);
      setSuccessKind(null);
      setErrorMsg("");
      setEmail("");
      setPlan("free");
      setServerAlreadySubscribed(false);
      setIsLoading(false);
      setView("subscribe");
      return;
    }

    setView(initialView === "support" && !paidEnabled ? "subscribe" : initialView);
    setPlan(initialView === "support" && paidEnabled ? "monthly" : "free");
    if (initialView === "signin") {
      setLoginResetToken((value) => value + 1);
    }
  }, [isOpen, initialView, paidEnabled]);

  useEffect(() => {
    if (!isOpen || view === "signin") return;

    if (isOwnerCheckLoading) return;

    const signedInEmail = getAuthEmail();
    if (signedInEmail) {
      setEmail((current) => current || signedInEmail);
    }

    if (isSiteOwner || (signedInEmail && isPublisherOwnerEmail(signedInEmail))) {
      setErrorMsg(OWNER_SUBSCRIBE_ERROR);
      setForceManualForm(true);
    }
  }, [isOpen, view, isSiteOwner, isOwnerCheckLoading]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), a[href], [tabindex='0']") || [],
      ).filter((element) => !element.matches(":disabled"));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const dialog = dialogRef.current;
    dialog?.addEventListener("keydown", onKeyDown);
    return () => {
      dialog?.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, onClose, view, successKind]);

  const signedInOwnsThisEmail = (() => {
    if (!hasAuthToken()) return false;
    const signedInEmail = getAuthEmail();
    return Boolean(signedInEmail && signedInEmail.toLowerCase() === email.trim().toLowerCase());
  })();

  const formatPaidDate = (unixSeconds: number) =>
    new Date(unixSeconds * 1000).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const hasCurrentPaid = signedInOwnsThisEmail && isPaidAccessActive(currentPayment);
  const paidAccessUntil = (() => {
    if (!hasCurrentPaid || !currentPayment?.canceledAt) return "";
    if (typeof currentPayment.endedAt !== "number" || currentPayment.endedAt <= 0) return "";
    return formatPaidDate(currentPayment.endedAt);
  })();

  const hasActivePaidPlan = hasCurrentPaid || serverAlreadySubscribed;
  const blockPaidPlans = hasActivePaidPlan;
  const isSupportView = paidEnabled && plan !== "free";

  if (!isOpen) return null;

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading || sendingRef.current) return;
    setErrorMsg("");

    if (!email) {
      setErrorMsg("Please enter your email address.");
      return;
    }

    if (!isValidEmail(email)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }

    if (isSiteOwner || isPublisherOwnerEmail(email)) {
      setErrorMsg(OWNER_SUBSCRIBE_ERROR);
      return;
    }

    if (isSupportView && blockPaidPlans) {
      setErrorMsg(t("subscribe.existingSupporter"));
      return;
    }

    const authToken = getAuthToken();
    const anonymous = !authToken;
    const anonymousEmailRequest = anonymous && !isSupportView;
    if (anonymousEmailRequest && Date.now() < requestUntil) return;
    if (anonymous && SystemConfig.turnstileSiteKey && !turnstileToken) {
      setErrorMsg("Please confirm you are human and try again.");
      return;
    }

    sendingRef.current = true;
    setIsLoading(true);
    try {
      const paidPlan = isSupportView ? (plan === "annual" ? "annual" : "monthly") : undefined;
      const response = await requestSubscribe(email.trim(), {
        token: authToken,
        ...(anonymous && turnstileToken ? { turnstileToken } : {}),
        ...(paidPlan ? { plan: paidPlan, successUrl: currentPageSuccessUrl() } : {}),
      });
      if (paidPlan) {
        if (response.data?.data?.alreadySubscribed === true) {
          setServerAlreadySubscribed(true);
          notifySubscriptionChanged();
          return;
        }
        const checkoutUrl = response.data?.data?.checkoutUrl;
        if (!checkoutUrl || typeof checkoutUrl !== "string") {
          setErrorMsg("Checkout did not return a payment URL.");
          return;
        }
        window.location.href = checkoutUrl;
        return;
      }

      // 200: already subscribed (logged-in matching email)
      // 201: confirmation email sent / pending
      if (anonymousEmailRequest) startCooldown();
      setSuccessKind(response.status === 201 ? "confirm" : "subscribed");
      if (response.status !== 201) setEmail("");
      notifySubscriptionChanged();
    } catch (err: any) {
      if (anonymousEmailRequest && err.response?.status === 429) {
        const retryAfter = err.response?.data?.data?.rateLimit?.retryAfter;
        startCooldown(typeof retryAfter === "number" && Number.isFinite(retryAfter) ? Math.max(60, Math.ceil(retryAfter)) : 60);
      }
      setTurnstileToken("");
      setTurnstileResetKey((value) => value + 1);
      if (handleAuthError(err)) {
        setForceManualForm(true);
        return;
      }
      const serverMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      setErrorMsg(serverMessage || "Something went wrong. Please try again later.");
    } finally {
      sendingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSuccessKind(null);
    setErrorMsg("");
    setEmail("");
    setPlan("free");
    setServerAlreadySubscribed(false);
    setForceManualForm(false);
    setTurnstileToken("");
    setTurnstileResetKey((value) => value + 1);
    setIsLoading(false);
    setView("subscribe");
    onClose();
  };

  const handleShowSignIn = () => {
    setView("signin");
    setLoginResetToken((value) => value + 1);
  };

  const monthlyLabel = paid?.monthly ? formatUsdFromCents(paid.monthly.price) : "";
  const annualLabel = paid?.annual ? formatUsdFromCents(paid.annual.price) : "";
  const submitKey = isSupportView ? "subscribe.continuePayment" : "subscribe.subscribe";
  const anonymousSubscribe = !getAuthToken();
  const emailRequestCoolingDown = anonymousSubscribe && !isSupportView && requestSeconds > 0;
  const turnstileRequired = anonymousSubscribe && SystemConfig.turnstileSiteKey.length > 0;

  return (
    <div className="fixed inset-0 z-9999 flex items-start justify-center p-4 pt-16 sm:pt-24 bg-overlay backdrop-blur-xs overflow-hidden overscroll-none">
      <div
        role="dialog"
        ref={dialogRef}
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="subscribe-dialog-title"
        className="relative w-full max-w-sm max-h-[calc(100dvh-6rem)] overflow-y-auto bg-dialog text-foreground border border-border rounded-xl p-5 sm:p-6 flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          id="subscribe-close-button"
          aria-label={t("subscribe.close")}
          data-i18n="subscribe.close"
          onClick={handleClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>

        {view === "signin" ? (
          <div className="w-full">
            <h2 id="subscribe-dialog-title" className="sr-only" data-i18n="subscribe.signIn">
              {t("subscribe.signIn")}
            </h2>
            <LoginForm
              showLogo={false}
              resetToken={loginResetToken}
              onCancel={() => setView("subscribe")}
              onSuccess={() => {
                handleClose();
                if (onSignInSuccess) onSignInSuccess();
              }}
            />
          </div>
        ) : successKind ? (
          <div className="flex flex-col items-center animate-fade-in">
            <div className="w-12 h-12 rounded-full bg-green/10 text-green flex items-center justify-center mb-4 border border-green/20">
              <CheckCircle2 className="w-6 h-6" />
            </div>

            <h3 id="subscribe-dialog-title" className="text-lg font-extrabold text-foreground mb-2">
              {successKind === "confirm" ? "Check your inbox" : "You are subscribed"}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
              {successKind === "confirm"
                ? "We have sent a confirmation email. Please check your inbox and confirm your subscription."
                : "You are on the list for this publication. New posts will arrive in your inbox."}
            </p>

            <button
              onClick={handleClose}
              className="w-full py-2.5 text-xs font-bold rounded-lg bg-primary text-primary-foreground hover:opacity-95 hover:shadow-md transition-all cursor-pointer border-0"
            >
              OK
            </button>
            {successKind === "confirm" && anonymousSubscribe ? (
              <button
                type="button"
                disabled={requestSeconds > 0}
                onClick={() => {
                  setSuccessKind(null);
                  setErrorMsg("");
                  setTurnstileToken("");
                  setTurnstileResetKey((value) => value + 1);
                }}
                className="w-full mt-2 py-2.5 text-xs font-semibold rounded-lg border border-border hover:bg-muted text-foreground transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requestSeconds > 0 ? `Request another email in ${requestSeconds}s` : "Request another email"}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="w-full flex flex-col items-center text-center">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-border/40 mb-4">
              <Mail className="w-5 h-5" />
            </div>

            <h3
              id="subscribe-dialog-title"
              className="font-extrabold text-base text-foreground leading-tight mb-1"
              data-i18n="subscribe.title"
            >
              {t("subscribe.title", { name: publicationName })}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-5 max-w-62.5" data-i18n="subscribe.description">
              {t("subscribe.description", { name: publicationName })}
            </p>

            <form onSubmit={handleSubscribe} className="w-full flex flex-col gap-2.5">
              {!hasAuthToken() ? (
                <p className="text-xs text-muted-foreground text-left">
                  Already have an account?{" "}
                  <button type="button" onClick={handleShowSignIn} className="font-semibold text-primary hover:underline cursor-pointer">
                    Sign in
                  </button>
                </p>
              ) : null}
              <input
                id="subscribe-email-input"
                type="email"
                aria-label={t("subscribe.email")}
                data-i18n="subscribe.email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setServerAlreadySubscribed(false);
                  if (errorMsg) setErrorMsg("");
                }}
                disabled={isLoading}
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-background/60 text-xs focus:outline-none focus:border-ring transition-all text-foreground placeholder:text-muted-foreground disabled:opacity-55"
              />

              {turnstileRequired ? (
                <TurnstileWidget
                  siteKey={SystemConfig.turnstileSiteKey}
                  resetKey={turnstileResetKey}
                  onToken={setTurnstileToken}
                  action="subscribe"
                />
              ) : null}

              {paidEnabled && hasCurrentPaid && currentPayment?.canceledAt ? (
                <p
                  className="w-full text-left text-xs leading-relaxed rounded-lg px-3 py-2 bg-primary/5 text-foreground"
                  data-i18n={paidAccessUntil ? "subscribe.supporterUntil" : "subscribe.supporterUntilPeriodEnd"}
                >
                  {t(paidAccessUntil ? "subscribe.supporterUntil" : "subscribe.supporterUntilPeriodEnd", { date: paidAccessUntil })}
                </p>
              ) : null}

              {paidEnabled && ((hasCurrentPaid && !currentPayment?.canceledAt) || (serverAlreadySubscribed && !hasCurrentPaid)) ? (
                <p
                  className="w-full text-left text-xs leading-relaxed rounded-lg px-3 py-2 bg-primary/5 text-foreground"
                  data-i18n="subscribe.existingSupporter"
                >
                  {t("subscribe.existingSupporter")}
                </p>
              ) : null}

              {paidEnabled ? (
                <fieldset className="w-full grid grid-cols-1 gap-2 text-left" disabled={isLoading}>
                  <legend className="sr-only" data-i18n="subscribe.optionsLabel">
                    {t("subscribe.optionsLabel")}
                  </legend>
                  {(
                    [
                      {
                        id: "free" as const,
                        labelKey: "subscribe.freeTitle" as const,
                        hintKey: null,
                        price: "",
                      },
                      {
                        id: "monthly" as const,
                        labelKey: "subscribe.monthlyPrice" as const,
                        hintKey: "subscribe.monthlySupportHint" as const,
                        price: monthlyLabel,
                      },
                      {
                        id: "annual" as const,
                        labelKey: "subscribe.annualPrice" as const,
                        hintKey: "subscribe.annualSupportHint" as const,
                        price: annualLabel,
                      },
                    ] as const
                  ).map((option) => {
                    const selected = plan === option.id;
                    const disabled = isLoading || (option.id !== "free" && blockPaidPlans);
                    return (
                      <label
                        key={option.id}
                        className={`flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg border text-left transition-all ${
                          disabled ? "opacity-55 cursor-not-allowed" : "cursor-pointer"
                        } ${selected ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}
                      >
                        <input
                          type="radio"
                          name="subscription-option"
                          value={option.id}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => {
                            setPlan(option.id);
                            setErrorMsg("");
                          }}
                          data-testid={`subscribe-plan-${option.id}`}
                          aria-labelledby={`subscribe-plan-${option.id}-label`}
                          aria-describedby={option.hintKey ? `subscribe-plan-${option.id}-hint` : undefined}
                          className="accent-primary"
                        />
                        <span className="flex flex-col gap-1 min-w-0">
                          <span id={`subscribe-plan-${option.id}-label`} className="text-xs font-semibold" data-i18n={option.labelKey}>
                            {t(option.labelKey, { price: option.price })}
                          </span>
                          {option.hintKey && (
                            <span
                              id={`subscribe-plan-${option.id}-hint`}
                              className="text-xs text-muted-foreground leading-relaxed"
                              data-i18n={option.hintKey}
                            >
                              {t(option.hintKey, { name: publicationName })}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : null}

              {errorMsg && <p className="text-[11px] text-red-500 font-medium px-1 -mt-1 leading-normal text-left w-full">{errorMsg}</p>}
              <div className="flex gap-2.5 w-full mt-2">
                <button
                  id="subscribe-cancel-button"
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-2.5 text-xs font-bold rounded-lg border border-border hover:bg-muted text-foreground transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  id="subscribe-submit-button"
                  type="submit"
                  disabled={
                    isLoading || emailRequestCoolingDown || (isSupportView && blockPaidPlans) || (turnstileRequired && !turnstileToken)
                  }
                  data-i18n={
                    emailRequestCoolingDown
                      ? undefined
                      : isLoading
                        ? isSupportView
                          ? "subscribe.redirecting"
                          : "subscribe.subscribing"
                        : submitKey
                  }
                  className="flex-1 py-2.5 text-xs font-bold rounded-lg bg-primary text-primary-foreground hover:opacity-95 hover:shadow-md transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-75 disabled:cursor-not-allowed border-0"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {t(isSupportView ? "subscribe.redirecting" : "subscribe.subscribing")}
                    </>
                  ) : emailRequestCoolingDown ? (
                    `Try again in ${requestSeconds}s`
                  ) : (
                    t(submitKey)
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
